import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import axios from 'axios';
import {
  ComposedChart, BarChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  Package, Users, BookOpen, Download, Activity, TrendingUp,
  AlertCircle, Loader2, ChevronDown, ChevronUp, Clock,
} from 'lucide-react';
import { PreviewRoleContext } from '../../context/PreviewRoleContext';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Brand colours (matching portal design system) ─────────────────────────────
const C = {
  dark:   '#0e2e2e',
  green:  '#7ab648',
  blue:   '#0078D4',
  ltBlue: '#50A0D0',
  amber:  '#F59E0B',
};

// ── Tab definitions ───────────────────────────────────────────────────────────
const ANALYTICS_TABS = [
  { id: 'overview',          label: 'Overview' },
  { id: 'csp-leaderboard',   label: 'CSP Leaderboard' },
  { id: 'stellar-reseller',  label: 'Stellar Reseller' },
  { id: 'training-velocity', label: 'Training Velocity' },
  { id: 'partner-tiers',     label: 'Partner Tiers' },
];

const OPERATIONS_TABS = [
  { id: 'workshops',         label: 'All Workshops' },
  { id: 'private-delivery',  label: 'Private Delivery Requests' },
  { id: 'lab-requests',      label: 'Lab Requests' },
  { id: 'syndication',       label: 'Syndication Requests' },
];

// Endpoint paths for operations tabs
const OPS_PATH = {
  'workshops':        'workshops',
  'private-delivery': 'private-delivery-requests',
  'lab-requests':     'lab-requests',
  'syndication':      'syndication-requests',
};

// ── Utilities ─────────────────────────────────────────────────────────────────
function fmt(n) { return (n ?? 0).toLocaleString(); }
function pct(n) { return n != null ? `${Number(n).toFixed(1)}%` : '—'; }
function fmtDate(iso) {
  if (!iso) return '—';
  const s = iso.includes('T') ? iso.split('T')[0] : iso;
  const d = new Date(s + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function normaliseArray(raw) {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.data)) return raw.data;
  return [];
}

// ── Small reusable components ─────────────────────────────────────────────────
function StarRating({ stars, max = 5 }) {
  return (
    <span style={{ fontSize: 14, letterSpacing: 1 }}>
      {Array.from({ length: max }, (_, i) => (
        <span key={i} style={{ color: i < (stars || 0) ? C.amber : '#d1d5db' }}>★</span>
      ))}
    </span>
  );
}

const TIER_CFG = {
  Champion:    { bg: '#dcfce7', color: '#166534', border: '#86efac' },
  Accelerator: { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' },
  Explorer:    { bg: '#f1f5f9', color: '#64748b', border: '#cbd5e1' },
};
function TierBadge({ tier }) {
  const t = TIER_CFG[tier] || TIER_CFG.Explorer;
  return (
    <span style={{ padding: '2px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 700, background: t.bg, color: t.color, border: `1px solid ${t.border}`, whiteSpace: 'nowrap' }}>
      {tier || '—'}
    </span>
  );
}

const STATUS_CFG = {
  scheduled: { bg: '#dbeafe', color: '#1e40af' },
  completed:  { bg: '#dcfce7', color: '#166534' },
  cancelled:  { bg: '#fee2e2', color: '#991b1b' },
  pending:    { bg: '#fef3c7', color: '#92400e' },
  approved:   { bg: '#dcfce7', color: '#166534' },
  rejected:   { bg: '#fee2e2', color: '#991b1b' },
};
function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status?.toLowerCase()] || { bg: '#f1f5f9', color: '#64748b' };
  return (
    <span style={{ padding: '2px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 700, background: cfg.bg, color: cfg.color, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
      {status || '—'}
    </span>
  );
}

function LoadingState() {
  return (
    <div data-testid="sib-loading" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '56px 0', color: '#64748b', fontSize: 14 }}>
      <Loader2 size={18} style={{ animation: 'spin 1s linear infinite', color: C.green }} />
      Loading data…
    </div>
  );
}

function ErrorBanner({ message }) {
  return (
    <div data-testid="sib-error" style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '16px 20px', background: '#fafafa', border: '1px solid #e2e8f0', borderRadius: 10, margin: '12px 0' }}>
      <AlertCircle size={17} color="#94a3b8" style={{ flexShrink: 0, marginTop: 1 }} />
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#475569', marginBottom: 2 }}>
          Skilling-in-a-Box data is temporarily unavailable. Please try again shortly.
        </div>
        {message && <div style={{ fontSize: 12, color: '#94a3b8' }}>{message}</div>}
      </div>
    </div>
  );
}

function DateRangePicker({ from, to, onFrom, onTo, prefix }) {
  const iStyle = { height: 30, padding: '0 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, color: '#1e293b', background: '#fff', outline: 'none' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Date range:</span>
      <input type="date" data-testid={`${prefix}-date-from`} value={from} onChange={e => onFrom(e.target.value)} style={iStyle} />
      <span style={{ color: '#94a3b8', fontSize: 12 }}>to</span>
      <input type="date" data-testid={`${prefix}-date-to`} value={to} onChange={e => onTo(e.target.value)} style={iStyle} />
      {(from || to) && (
        <button data-testid={`${prefix}-clear-dates`} onClick={() => { onFrom(''); onTo(''); }}
          style={{ height: 30, padding: '0 10px', fontSize: 12, background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', color: '#94a3b8' }}>
          Clear
        </button>
      )}
    </div>
  );
}

function SubTabBar({ tabs, active, onChange, testPrefix }) {
  return (
    <div data-testid={`${testPrefix}-bar`} style={{ borderBottom: '2px solid #e2e8f0', display: 'flex', gap: 0, marginBottom: 20, overflowX: 'auto' }}>
      {tabs.map(t => (
        <button key={t.id} data-testid={`${testPrefix}-${t.id}`} onClick={() => onChange(t.id)}
          style={{ padding: '8px 18px', fontSize: 13, fontWeight: 600, border: 'none', background: 'none', cursor: 'pointer', whiteSpace: 'nowrap', borderBottom: active === t.id ? `2px solid ${C.green}` : '2px solid transparent', color: active === t.id ? C.dark : '#64748b', marginBottom: -2, transition: 'color 0.15s' }}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ── Analytics › Overview ──────────────────────────────────────────────────────
function OverviewTab({ data, from, to, onFrom, onTo, onJumpToOps }) {
  const { kpis = {}, pending_counts = {}, top_downloaded_courses = [], learners_by_organization = [], top_courses_by_learners = [] } = data || {};
  const chartData = (top_downloaded_courses || []).slice(0, 10).map(r => ({
    name: (r.course_title || '').length > 32 ? r.course_title.slice(0, 32) + '…' : (r.course_title || '—'),
    downloads: r.download_count || 0,
  }));

  return (
    <>
      {/* Date range */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, flexWrap: 'wrap' }}>
        <DateRangePicker from={from} to={to} onFrom={onFrom} onTo={onTo} prefix="overview" />
        {(from || to) && (
          <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
            Total Partners &amp; Courses are always all-time; other KPIs reflect the selected range.
          </span>
        )}
      </div>

      {/* 5 KPI cards */}
      <div className="stats-grid" data-testid="sib-overview-kpis" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', marginBottom: 20 }}>
        {[
          { label: 'Total Courses',     val: kpis.total_courses,          icon: BookOpen,   accent: C.dark,   tid: 'sib-kpi-courses' },
          { label: 'Program Partners',  val: kpis.total_partners,         icon: Users,      accent: C.blue,   tid: 'sib-kpi-partners' },
          { label: 'Total Downloads',   val: kpis.total_downloads,        icon: Download,   accent: C.green,  tid: 'sib-kpi-downloads' },
          { label: 'Workshops',         val: kpis.total_workshops,        icon: Activity,   accent: C.ltBlue, tid: 'sib-kpi-workshops' },
          { label: 'Trained Learners',  val: kpis.total_trained_learners, icon: TrendingUp, accent: C.amber,  tid: 'sib-kpi-learners' },
        ].map(({ label, val, icon: Icon, accent, tid }) => (
          <div key={tid} className="stat-card" data-testid={tid} style={{ borderTop: `3px solid ${accent}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div className="stat-label">{label}</div>
              <Icon size={15} color={accent} />
            </div>
            <div className="stat-value">{fmt(val)}</div>
          </div>
        ))}
      </div>

      {/* Pending action cards */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Pending Actions</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {[
            { label: 'Private Delivery Requests', count: pending_counts.private_workshop_requests, accent: C.amber,  tid: 'sib-pending-private',      opTab: 'private-delivery' },
            { label: 'Lab Requests',              count: pending_counts.lab_requests,              accent: C.blue,   tid: 'sib-pending-labs',          opTab: 'lab-requests' },
            { label: 'Syndication Requests',      count: pending_counts.syndication_requests,      accent: C.ltBlue, tid: 'sib-pending-syndication',   opTab: 'syndication' },
          ].map(({ label, count, accent, tid, opTab }) => (
            <button key={tid} data-testid={tid} onClick={() => onJumpToOps(opTab)}
              style={{ flex: '1 1 160px', maxWidth: 220, padding: '14px 18px', background: '#fff', border: `1px solid ${accent}40`, borderRadius: 10, cursor: 'pointer', textAlign: 'left', transition: 'box-shadow 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 4px 16px ${accent}28`; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: accent, marginBottom: 2 }}>{count ?? 0}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>{label}</div>
              <div style={{ fontSize: 11, color: accent, marginTop: 4, fontWeight: 600 }}>View Requests →</div>
            </button>
          ))}
        </div>
      </div>

      {/* Top Downloaded Courses — horizontal bar chart */}
      {chartData.length > 0 && (
        <div className="report-card" data-testid="sib-top-downloads-chart" style={{ padding: '16px 16px 8px', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>Top Downloaded Courses</div>
          <ResponsiveContainer width="100%" height={Math.max(180, chartData.length * 28 + 40)}>
            <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 56, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#475569' }} width={200} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e2e8f0' }} formatter={v => [fmt(v), 'Downloads']} />
              <Bar dataKey="downloads" name="Downloads" fill={C.green} radius={[0, 3, 3, 0]} maxBarSize={14}
                label={{ position: 'right', fontSize: 11, fill: '#475569' }} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Learners by Organisation */}
      {learners_by_organization.length > 0 && (
        <div className="report-card" data-testid="sib-learners-by-org" style={{ overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ padding: '14px 16px 10px', fontSize: 13, fontWeight: 600, color: '#1e293b', borderBottom: '1px solid #f1f5f9' }}>
            Learners by Organisation
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#fafafa', borderBottom: '1px solid #f1f5f9' }}>
                  {['Organisation', 'Workshops', 'Avg Completion %', 'Learners'].map(h => (
                    <th key={h} style={{ padding: '9px 16px', textAlign: h !== 'Organisation' ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {learners_by_organization.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f8fafc' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                    <td style={{ padding: '9px 16px', fontFamily: 'monospace', color: '#1e293b', fontWeight: 600 }}>{r.organization || '—'}</td>
                    <td style={{ padding: '9px 16px', textAlign: 'right', color: '#64748b' }}>{fmt(r.workshops)}</td>
                    <td style={{ padding: '9px 16px', textAlign: 'right', color: '#475569' }}>{pct(r.avg_completion_rate)}</td>
                    <td style={{ padding: '9px 16px', textAlign: 'right', fontWeight: 700, color: C.green }}>{fmt(r.total_learners)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Courses by Learners */}
      {top_courses_by_learners.length > 0 && (
        <div className="report-card" data-testid="sib-top-courses-learners" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px 10px', fontSize: 13, fontWeight: 600, color: '#1e293b', borderBottom: '1px solid #f1f5f9' }}>
            Top Courses by Trained Learners
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#fafafa', borderBottom: '1px solid #f1f5f9' }}>
                  {['Course', 'Solution Area', 'Workshops', 'Learners'].map(h => (
                    <th key={h} style={{ padding: '9px 16px', textAlign: h === 'Workshops' || h === 'Learners' ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {top_courses_by_learners.map((r, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f8fafc' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                    <td style={{ padding: '9px 16px', color: '#1e293b', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.course_title || '—'}</td>
                    <td style={{ padding: '9px 16px' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 600, background: '#0078d422', color: C.blue }}>
                        {r.solution_area || '—'}
                      </span>
                    </td>
                    <td style={{ padding: '9px 16px', textAlign: 'right', color: '#64748b' }}>{fmt(r.total_workshops)}</td>
                    <td style={{ padding: '9px 16px', textAlign: 'right', fontWeight: 700, color: C.green }}>{fmt(r.total_learners)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

// ── Analytics › Leaderboard (shared CSP + Stellar) ────────────────────────────
const RANK_COLORS = ['#F59E0B', '#94A3B8', '#C87941'];

function LeaderboardTable({ items, domainField, tooltip, testId }) {
  const [saFilter, setSaFilter] = useState('all');

  const solutionAreas = useMemo(() => {
    const seen = new Set();
    items.forEach(r => { if (r.solution_area) seen.add(r.solution_area); });
    return ['all', ...Array.from(seen).sort()];
  }, [items]);

  const filtered = saFilter === 'all' ? items : items.filter(r => r.solution_area === saFilter);
  const iStyle = { height: 30, padding: '0 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, color: '#1e293b', background: '#fff', cursor: 'pointer' };

  return (
    <div className="report-card" data-testid={testId} style={{ overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 12px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>Partner Leaderboard</div>
          {tooltip && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2, fontStyle: 'italic' }}>{tooltip}</div>}
        </div>
        {solutionAreas.length > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#64748b' }}>Solution Area:</span>
            <select value={saFilter} onChange={e => setSaFilter(e.target.value)} style={iStyle} data-testid={`${testId}-sa-filter`}>
              {solutionAreas.map(s => <option key={s} value={s}>{s === 'all' ? 'All' : s}</option>)}
            </select>
          </div>
        )}
      </div>

      {filtered.length === 0 ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>No data available.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#fafafa', borderBottom: '1px solid #f1f5f9' }}>
                {['Rank', 'Domain', 'Learners', 'Workshops', 'Last Training', 'Score', 'Stars'].map(h => (
                  <th key={h} style={{ padding: '9px 14px', textAlign: ['Rank', 'Learners', 'Workshops', 'Score'].includes(h) ? 'center' : 'left', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => {
                const rank = r.rank != null ? r.rank - 1 : i;
                const rankColor = rank < 3 ? RANK_COLORS[rank] : 'transparent';
                return (
                  <tr key={i} data-testid={`${testId}-row-${i}`}
                    style={{ borderBottom: '1px solid #f8fafc', borderLeft: `3px solid ${rankColor}` }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                    <td style={{ padding: '9px 14px', textAlign: 'center', fontWeight: 800, color: rankColor !== 'transparent' ? rankColor : '#94a3b8', fontSize: 14 }}>{r.rank || i + 1}</td>
                    <td style={{ padding: '9px 14px', fontFamily: 'monospace', fontWeight: 600, color: '#1e293b' }}>{r[domainField] || '—'}</td>
                    <td style={{ padding: '9px 14px', textAlign: 'center', fontWeight: 700, color: C.green }}>{fmt(r.learners_trained)}</td>
                    <td style={{ padding: '9px 14px', textAlign: 'center', color: '#475569' }}>{fmt(r.workshops_count)}</td>
                    <td style={{ padding: '9px 14px', color: '#64748b', whiteSpace: 'nowrap' }}>{fmtDate(r.last_training)}</td>
                    <td style={{ padding: '9px 14px', textAlign: 'center', fontWeight: 700, color: C.dark }}>{r.score ?? '—'}</td>
                    <td style={{ padding: '9px 14px' }}><StarRating stars={r.stars || 0} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CSPLeaderboardTab({ data }) {
  return <LeaderboardTable items={normaliseArray(data)} domainField="partner_domain" testId="sib-csp-table" />;
}

function StellarResellerTab({ data }) {
  return (
    <LeaderboardTable
      items={normaliseArray(data)}
      domainField="reseller_domain"
      tooltip="Reseller/customer organisations trained by your partners"
      testId="sib-stellar-table"
    />
  );
}

// ── Analytics › Training Velocity ─────────────────────────────────────────────
const VELOCITY_PRESETS = [
  { id: 'last3m',  label: 'Last 3m',  months: 3 },
  { id: 'last6m',  label: 'Last 6m',  months: 6 },
  { id: 'last12m', label: 'Last 12m', months: 12 },
  { id: 'alltime', label: 'All Time', months: null },
];

function TrainingVelocityTab({ data, from, to, onFrom, onTo }) {
  const [activePreset, setActivePreset] = useState('last12m');
  const items = normaliseArray(data);
  const totalWorkshops = items.reduce((s, r) => s + (r.workshops || 0), 0);
  const totalLearners  = items.reduce((s, r) => s + (r.learners  || 0), 0);

  const applyPreset = (preset) => {
    setActivePreset(preset.id);
    if (preset.months === null) {
      onFrom('');
      onTo('');
    } else {
      const today = new Date();
      const start = new Date(today);
      start.setMonth(start.getMonth() - preset.months);
      onFrom(start.toISOString().split('T')[0]);
      onTo(today.toISOString().split('T')[0]);
    }
  };

  const handleManualFrom = (v) => { setActivePreset(null); onFrom(v); };
  const handleManualTo   = (v) => { setActivePreset(null); onTo(v); };

  return (
    <>
      <div data-testid="velocity-filter-bar" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, flexWrap: 'wrap' }}>
        {/* Quick preset buttons */}
        <div style={{ display: 'flex', gap: 4 }}>
          {VELOCITY_PRESETS.map(p => (
            <button key={p.id} data-testid={`velocity-preset-${p.id}`} onClick={() => applyPreset(p)}
              style={{ height: 30, padding: '0 14px', fontSize: 12, fontWeight: 600, border: `1px solid ${activePreset === p.id ? C.green : '#e2e8f0'}`, borderRadius: 6, cursor: 'pointer', background: activePreset === p.id ? C.dark : '#fff', color: activePreset === p.id ? C.green : '#64748b', transition: 'all 0.15s', whiteSpace: 'nowrap' }}>
              {p.label}
            </button>
          ))}
        </div>
        {/* Divider */}
        <div style={{ width: 1, height: 22, background: '#e2e8f0', flexShrink: 0 }} />
        {/* Custom date range */}
        <DateRangePicker from={from} to={to} onFrom={handleManualFrom} onTo={handleManualTo} prefix="velocity" />
      </div>

      {items.length > 0 && (
        <div style={{ fontSize: 13, color: '#475569', marginBottom: 14, fontWeight: 500 }}>
          <span style={{ fontWeight: 700, color: C.dark }}>{fmt(totalWorkshops)}</span> workshops ·{' '}
          <span style={{ fontWeight: 700, color: C.green }}>{fmt(totalLearners)}</span> learners in selected period
        </div>
      )}

      <div className="report-card" data-testid="sib-velocity-chart" style={{ padding: '16px 16px 8px' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 8 }}>Training Activity Over Time</div>
        {items.length === 0 ? (
          <div style={{ padding: '32px 0', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>No data for selected period.</div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={items} margin={{ top: 4, right: 56, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748b' }} tickLine={false} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} width={38} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} width={48} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e2e8f0' }} />
              <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
              <Bar yAxisId="left" dataKey="workshops" name="Workshops" fill={C.dark} radius={[3, 3, 0, 0]} maxBarSize={24} />
              <Line yAxisId="right" dataKey="learners" name="Learners" stroke={C.green} strokeWidth={2.5} dot={{ r: 3, fill: C.green }} activeDot={{ r: 5 }} type="monotone" />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </>
  );
}

// ── Analytics › Partner Tiers ─────────────────────────────────────────────────
function PartnerTiersTab({ data }) {
  const items = normaliseArray(data);
  const sorted = [...items].sort((a, b) => (b.score || 0) - (a.score || 0));
  const counts = { Champion: 0, Accelerator: 0, Explorer: 0 };
  sorted.forEach(r => { const t = r.tier || 'Explorer'; if (t in counts) counts[t]++; });

  return (
    <>
      <div data-testid="sib-tier-summary" style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {Object.entries(counts).map(([tier, n]) => (
          <div key={tier} style={{ padding: '12px 20px', borderRadius: 10, background: '#fff', border: `1px solid ${TIER_CFG[tier]?.border || '#e2e8f0'}`, display: 'flex', alignItems: 'center', gap: 12 }}>
            <TierBadge tier={tier} />
            <span style={{ fontSize: 24, fontWeight: 800, color: TIER_CFG[tier]?.color || '#64748b' }}>{n}</span>
          </div>
        ))}
      </div>

      <div className="report-card" data-testid="sib-partner-tiers-table" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px 10px', fontSize: 13, fontWeight: 600, color: '#1e293b', borderBottom: '1px solid #f1f5f9' }}>
          All Partners — sorted by score
        </div>
        {sorted.length === 0 ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>No data available.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#fafafa', borderBottom: '1px solid #f1f5f9' }}>
                  {['Partner Domain', 'Tier', 'Score', 'Workshops', 'Learners', 'Last Training'].map(h => (
                    <th key={h} style={{ padding: '9px 14px', textAlign: ['Score', 'Workshops', 'Learners'].includes(h) ? 'center' : 'left', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((r, i) => (
                  <tr key={i} data-testid={`sib-tier-row-${i}`}
                    style={{ borderBottom: '1px solid #f8fafc' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                    <td style={{ padding: '9px 14px', fontFamily: 'monospace', fontWeight: 600, color: '#1e293b' }}>{r.partner_domain || '—'}</td>
                    <td style={{ padding: '9px 14px' }}><TierBadge tier={r.tier} /></td>
                    <td style={{ padding: '9px 14px', textAlign: 'center', fontWeight: 700, color: C.dark }}>{r.score ?? '—'}</td>
                    <td style={{ padding: '9px 14px', textAlign: 'center', color: '#64748b' }}>{fmt(r.workshops)}</td>
                    <td style={{ padding: '9px 14px', textAlign: 'center', fontWeight: 700, color: C.green }}>{fmt(r.learners)}</td>
                    <td style={{ padding: '9px 14px', color: '#64748b' }}>{fmtDate(r.last_training)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// ── Operations › All Workshops ────────────────────────────────────────────────
function WorkshopsTab({ data }) {
  const allItems = normaliseArray(data);
  const [status, setStatus]   = useState('');
  const [location, setLocation] = useState('');
  const [from, setFrom]       = useState('');
  const [to, setTo]           = useState('');

  const filtered = allItems.filter(r => {
    if (status   && r.status !== status) return false;
    if (location && !r.location?.toLowerCase().includes(location.toLowerCase())) return false;
    const d = r.delivery_date?.split('T')[0] || '';
    if (from && d && d < from) return false;
    if (to   && d && d > to)   return false;
    return true;
  });

  const iStyle = { height: 30, padding: '0 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, color: '#1e293b', background: '#fff' };

  return (
    <>
      <div data-testid="sib-workshops-filters" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 16 }}>
        <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...iStyle, width: 140, cursor: 'pointer' }} data-testid="sib-wk-status">
          <option value="">All Statuses</option>
          <option value="scheduled">Scheduled</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <input type="text" placeholder="Search location…" value={location} onChange={e => setLocation(e.target.value)}
          style={{ ...iStyle, width: 160 }} data-testid="sib-wk-location" />
        <DateRangePicker from={from} to={to} onFrom={setFrom} onTo={setTo} prefix="workshop" />
      </div>

      <div className="report-card" data-testid="sib-workshops-table" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>Workshops</span>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>{filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>
        </div>
        {filtered.length === 0 ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>No workshops match current filters.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#fafafa', borderBottom: '1px solid #f1f5f9' }}>
                  {['Course', 'Partner Domain', 'Trainer', 'Date', 'Location', 'Expected', 'Actual', 'Lab', 'Status'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={r.id || i} style={{ borderBottom: '1px solid #f8fafc' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                    <td style={{ padding: '8px 12px', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#1e293b', fontWeight: 500 }}>{r.course_title || '—'}</td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace', color: '#475569', fontSize: 11 }}>{r.partner_domain || '—'}</td>
                    <td style={{ padding: '8px 12px', color: '#475569' }}>{r.trainer_name || '—'}</td>
                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', color: '#64748b' }}>{fmtDate(r.delivery_date)}</td>
                    <td style={{ padding: '8px 12px', color: '#64748b' }}>{r.location || '—'}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'center', color: '#475569' }}>{fmt(r.expected_attendees)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: C.dark }}>{fmt(r.actual_attendees)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'center', color: '#94a3b8' }}>{r.lab_consumption ?? '—'}</td>
                    <td style={{ padding: '8px 12px' }}><StatusBadge status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// ── Operations › Private Delivery Requests ────────────────────────────────────
function PrivateDeliveryTab({ data }) {
  const allItems = normaliseArray(data);
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId]     = useState(null);
  const STATUS_TABS = ['all', 'pending', 'approved', 'rejected'];
  const filtered = statusFilter === 'all' ? allItems : allItems.filter(r => r.status === statusFilter);

  return (
    <>
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
        {STATUS_TABS.map(s => {
          const cnt = s === 'all' ? allItems.length : allItems.filter(r => r.status === s).length;
          return (
            <button key={s} data-testid={`sib-pd-filter-${s}`} onClick={() => setStatusFilter(s)}
              style={{ height: 32, padding: '0 16px', fontSize: 12, fontWeight: 600, border: `1px solid ${statusFilter === s ? C.green : '#e2e8f0'}`, borderRadius: 6, cursor: 'pointer', background: statusFilter === s ? C.dark : '#fff', color: statusFilter === s ? C.green : '#64748b', textTransform: 'capitalize', transition: 'all 0.15s' }}>
              {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)} ({cnt})
            </button>
          );
        })}
      </div>

      <div className="report-card" data-testid="sib-private-delivery-table" style={{ overflow: 'hidden' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>No requests found.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#fafafa', borderBottom: '1px solid #f1f5f9' }}>
                  {['Status', 'Course of Interest', 'Organisation', 'Requested By', 'Expected', 'Timeframe', 'Submitted', ''].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const rowId = r.id || i;
                  const isOpen = expandedId === rowId;
                  return (
                    <React.Fragment key={rowId}>
                      <tr style={{ borderBottom: isOpen ? 'none' : '1px solid #f8fafc', background: isOpen ? '#f8fafc' : '' }}
                        onMouseEnter={e => { if (!isOpen) e.currentTarget.style.background = '#f8fafc'; }}
                        onMouseLeave={e => { if (!isOpen) e.currentTarget.style.background = ''; }}>
                        <td style={{ padding: '9px 12px' }}><StatusBadge status={r.status} /></td>
                        <td style={{ padding: '9px 12px', color: '#1e293b', fontWeight: 500, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.course_of_interest || '—'}</td>
                        <td style={{ padding: '9px 12px', color: '#475569' }}>{r.organization || '—'}</td>
                        <td style={{ padding: '9px 12px', color: '#475569' }}>{r.requester_name || '—'}</td>
                        <td style={{ padding: '9px 12px', textAlign: 'center', color: '#64748b' }}>{fmt(r.expected_attendance)}</td>
                        <td style={{ padding: '9px 12px', color: '#64748b', whiteSpace: 'nowrap' }}>{r.preferred_timeframe || '—'}</td>
                        <td style={{ padding: '9px 12px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</td>
                        <td style={{ padding: '9px 12px' }}>
                          <button data-testid={`sib-pd-expand-${i}`} onClick={() => setExpandedId(isOpen ? null : rowId)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.green, fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
                            {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                            {isOpen ? 'Less' : 'More'}
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td colSpan={8} style={{ padding: '12px 20px 16px', background: '#f8fafc' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
                              <div>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Expectations</div>
                                <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>{r.expectations || '—'}</div>
                              </div>
                              <div>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Admin Notes</div>
                                <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>{r.admin_notes || '—'}</div>
                              </div>
                              {r.preferred_language && (
                                <div>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Preferred Language</div>
                                  <div style={{ fontSize: 13, color: '#475569' }}>{r.preferred_language}</div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// ── Operations › Lab Requests ─────────────────────────────────────────────────
function LabRequestsTab({ data }) {
  const allItems = normaliseArray(data);
  const [statusFilter, setStatusFilter] = useState('all');
  const STATUS_TABS = ['all', 'pending', 'approved', 'rejected', 'cancelled'];
  const filtered = statusFilter === 'all' ? allItems : allItems.filter(r => r.status === statusFilter);

  return (
    <>
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
        {STATUS_TABS.map(s => {
          const cnt = s === 'all' ? allItems.length : allItems.filter(r => r.status === s).length;
          return (
            <button key={s} data-testid={`sib-lab-filter-${s}`} onClick={() => setStatusFilter(s)}
              style={{ height: 32, padding: '0 14px', fontSize: 12, fontWeight: 600, border: `1px solid ${statusFilter === s ? C.green : '#e2e8f0'}`, borderRadius: 6, cursor: 'pointer', background: statusFilter === s ? C.dark : '#fff', color: statusFilter === s ? C.green : '#64748b', textTransform: 'capitalize', transition: 'all 0.15s' }}>
              {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)} ({cnt})
            </button>
          );
        })}
      </div>

      <div className="report-card" data-testid="sib-lab-requests-table" style={{ overflow: 'hidden' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>No lab requests found.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#fafafa', borderBottom: '1px solid #f1f5f9' }}>
                  {['Course', 'Partner Org', 'Submitted By', 'Workshop Date', 'Lab Attendees', 'Status', 'Reviewed By', 'Reviewed Date'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={r.id || i} style={{ borderBottom: '1px solid #f8fafc' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                    <td style={{ padding: '8px 12px', color: '#1e293b', fontWeight: 500, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.course_title || '—'}</td>
                    <td style={{ padding: '8px 12px', color: '#475569' }}>{r.organization || '—'}</td>
                    <td style={{ padding: '8px 12px', color: '#475569' }}>{r.requester_name || '—'}</td>
                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', color: '#64748b' }}>{fmtDate(r.workshop_date)}</td>
                    <td style={{ padding: '8px 12px', textAlign: 'center', color: '#475569' }}>{fmt(r.expected_lab_attendees)}</td>
                    <td style={{ padding: '8px 12px' }}><StatusBadge status={r.status} /></td>
                    <td style={{ padding: '8px 12px', color: '#94a3b8', fontSize: 11 }}>{r.reviewed_by || '—'}</td>
                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', color: '#94a3b8' }}>{fmtDate(r.reviewed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// ── Operations › Syndication Requests ────────────────────────────────────────
function SyndicationTab({ data }) {
  const allItems = normaliseArray(data);
  const [statusFilter, setStatusFilter] = useState('all');
  const STATUS_TABS = ['all', 'pending', 'approved', 'rejected'];
  const filtered = statusFilter === 'all' ? allItems : allItems.filter(r => r.status === statusFilter);

  return (
    <>
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
        {STATUS_TABS.map(s => {
          const cnt = s === 'all' ? allItems.length : allItems.filter(r => r.status === s).length;
          return (
            <button key={s} data-testid={`sib-syn-filter-${s}`} onClick={() => setStatusFilter(s)}
              style={{ height: 32, padding: '0 14px', fontSize: 12, fontWeight: 600, border: `1px solid ${statusFilter === s ? C.green : '#e2e8f0'}`, borderRadius: 6, cursor: 'pointer', background: statusFilter === s ? C.dark : '#fff', color: statusFilter === s ? C.green : '#64748b', textTransform: 'capitalize', transition: 'all 0.15s' }}>
              {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)} ({cnt})
            </button>
          );
        })}
      </div>

      <div className="report-card" data-testid="sib-syndication-table" style={{ overflow: 'hidden' }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>No syndication requests found.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#fafafa', borderBottom: '1px solid #f1f5f9' }}>
                  {['Name', 'Organisation', 'Email', 'LMS Platform', 'SCORM Version', 'Timeframe', 'Status', 'Submitted'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => (
                  <tr key={r.id || i} style={{ borderBottom: '1px solid #f8fafc' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = ''; }}>
                    <td style={{ padding: '8px 12px', color: '#1e293b', fontWeight: 500 }}>{r.name || '—'}</td>
                    <td style={{ padding: '8px 12px', color: '#475569' }}>{r.organization || '—'}</td>
                    <td style={{ padding: '8px 12px', color: '#64748b', fontSize: 11 }}>{r.email || '—'}</td>
                    <td style={{ padding: '8px 12px', color: '#475569' }}>{r.lms_platform || '—'}</td>
                    <td style={{ padding: '8px 12px', color: '#475569' }}>{r.scorm_version || '—'}</td>
                    <td style={{ padding: '8px 12px', color: '#64748b', whiteSpace: 'nowrap' }}>{r.timeframe || '—'}</td>
                    <td style={{ padding: '8px 12px' }}><StatusBadge status={r.status} /></td>
                    <td style={{ padding: '8px 12px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// ── Operations Rollup KPI Groups (Analytics tab, visible to all users) ────────
function OperationsRollupKPIs({ data, loading }) {
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 0', color: '#64748b', fontSize: 13 }}>
      <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
      Loading operations summary…
    </div>
  );
  if (!data) return null;

  const groups = [
    {
      label: 'Workshops',
      accent: C.dark,
      items: [
        { label: 'Total',     val: data.workshops?.total,     tid: 'ops-rollup-ws-total' },
        { label: 'Scheduled', val: data.workshops?.scheduled, tid: 'ops-rollup-ws-scheduled' },
        { label: 'Completed', val: data.workshops?.completed, tid: 'ops-rollup-ws-completed' },
        { label: 'Cancelled', val: data.workshops?.cancelled, tid: 'ops-rollup-ws-cancelled' },
      ],
    },
    {
      label: 'Private Delivery Requests',
      accent: C.blue,
      items: [
        { label: 'Total',    val: data.private_delivery_requests?.total,    tid: 'ops-rollup-pd-total' },
        { label: 'Pending',  val: data.private_delivery_requests?.pending,  tid: 'ops-rollup-pd-pending' },
        { label: 'Approved', val: data.private_delivery_requests?.approved, tid: 'ops-rollup-pd-approved' },
        { label: 'Rejected', val: data.private_delivery_requests?.rejected, tid: 'ops-rollup-pd-rejected' },
      ],
    },
    {
      label: 'Lab Requests',
      accent: C.green,
      items: [
        { label: 'Total',     val: data.lab_requests?.total,     tid: 'ops-rollup-lr-total' },
        { label: 'Pending',   val: data.lab_requests?.pending,   tid: 'ops-rollup-lr-pending' },
        { label: 'Approved',  val: data.lab_requests?.approved,  tid: 'ops-rollup-lr-approved' },
        { label: 'Rejected',  val: data.lab_requests?.rejected,  tid: 'ops-rollup-lr-rejected' },
        { label: 'Cancelled', val: data.lab_requests?.cancelled, tid: 'ops-rollup-lr-cancelled' },
      ],
    },
    {
      label: 'Syndication Requests',
      accent: C.ltBlue,
      items: [
        { label: 'Total',    val: data.syndication_requests?.total,    tid: 'ops-rollup-sr-total' },
        { label: 'Pending',  val: data.syndication_requests?.pending,  tid: 'ops-rollup-sr-pending' },
        { label: 'Approved', val: data.syndication_requests?.approved, tid: 'ops-rollup-sr-approved' },
        { label: 'Rejected', val: data.syndication_requests?.rejected, tid: 'ops-rollup-sr-rejected' },
      ],
    },
  ];

  const allZero =
    data.workshops?.total === 0 &&
    data.private_delivery_requests?.total === 0 &&
    data.lab_requests?.total === 0 &&
    data.syndication_requests?.total === 0;

  return (
    <div data-testid="ops-rollup-kpis" style={{ marginTop: 28, paddingTop: 24, borderTop: '1px solid #e2e8f0' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
        Operations Summary
      </div>

      {allZero && (
        <div
          data-testid="ops-rollup-sync-banner"
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 12px', marginBottom: 16, borderRadius: 6,
            background: 'rgba(241, 245, 249, 0.9)',
            border: '1px solid #e2e8f0',
            borderLeft: '3px solid #94a3b8',
          }}
        >
          <Clock size={13} color="#94a3b8" style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 12, color: '#64748b', lineHeight: 1.4 }}>
            Operations data syncing soon — counts will populate when available.
          </span>
        </div>
      )}
      {groups.map(group => (
        <div key={group.label} style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: group.accent, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {group.label}
          </div>
          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' }}>
            {group.items.map(({ label, val, tid }) => (
              <div key={tid} className="stat-card" data-testid={tid} style={{ borderTop: `3px solid ${group.accent}` }}>
                <div className="stat-label">{label}</div>
                <div className="stat-value">{fmt(val)}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function SkillingInABox() {
  const [topTab,        setTopTab]        = useState('analytics');
  const [analyticsTab,  setAnalyticsTab]  = useState('overview');
  const [operationsTab, setOperationsTab] = useState('workshops');

  // PII gate — read from session; overridden by preview role if active
  const previewRole = useContext(PreviewRoleContext);
  const [sessionPiiAllowed, setSessionPiiAllowed] = useState(false);
  // Derived: preview scope overrides the real session value
  const piiAllowed = previewRole
    ? previewRole.scope?.show_pii === true
    : sessionPiiAllowed;

  // Date filter states (only for tabs that need them)
  const [overviewFrom, setOverviewFrom] = useState('');
  const [overviewTo,   setOverviewTo]   = useState('');
  const [velocityFrom, setVelocityFrom] = useState('');
  const [velocityTo,   setVelocityTo]   = useState('');

  // Operations rollup KPI state
  const [opsRollup, setOpsRollup] = useState({ loading: false, error: null, data: null });

  // Data cache: { [key]: { data, loading, error } }
  const [cache, setCache] = useState({});

  const fetchData = useCallback(async (key, path, params = {}) => {
    setCache(prev => ({ ...prev, [key]: { ...(prev[key] || {}), loading: true, error: null } }));
    try {
      const cleanParams = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null));
      const { data } = await axios.get(`${API}/api/lup/v2/report/skilling-box/${path}`, {
        params: cleanParams,
        withCredentials: true,
      });
      if (data?.success === false) {
        setCache(prev => ({ ...prev, [key]: { data: null, loading: false, error: data.message || 'Data unavailable' } }));
      } else {
        setCache(prev => ({ ...prev, [key]: { data, loading: false, error: null } }));
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.response?.data?.message || 'Skilling-in-a-Box data is temporarily unavailable. Please try again shortly.';
      setCache(prev => ({ ...prev, [key]: { data: null, loading: false, error: msg } }));
    }
  }, []);

  // Fetch pii_allowed from session on mount
  useEffect(() => {
    axios.get(`${API}/api/auth/me`, { withCredentials: true })
      .then(r => setSessionPiiAllowed(r.data?.pii_allowed === true))
      .catch(() => {});
  }, []);

  // Gate: if Operations tab is active but PII is not allowed, redirect to Analytics
  useEffect(() => {
    if (topTab === 'operations' && !piiAllowed) {
      setTopTab('analytics');
    }
  }, [piiAllowed, topTab]);

  // Fetch operations rollup whenever Analytics tab is active or date filters change
  useEffect(() => {
    if (topTab !== 'analytics') return;
    const params = new URLSearchParams();
    if (overviewFrom) params.set('date_from', overviewFrom);
    if (overviewTo)   params.set('date_to',   overviewTo);
    const query = params.toString() ? `?${params.toString()}` : '';
    setOpsRollup(prev => ({ ...prev, loading: true, error: null }));
    axios.get(`${API}/api/siab/operations-summary${query}`, { withCredentials: true })
      .then(r => setOpsRollup({ loading: false, error: null, data: r.data }))
      .catch(e => setOpsRollup({ loading: false, error: e.message, data: null }));
  }, [topTab, overviewFrom, overviewTo]); // eslint-disable-line

  // Load data when tab or filters change
  useEffect(() => {
    if (topTab === 'analytics') {
      if (analyticsTab === 'overview') {
        fetchData('overview', 'overview', { from_date: overviewFrom, to_date: overviewTo });
      } else if (analyticsTab === 'training-velocity') {
        fetchData('training-velocity', 'training-velocity', { from_date: velocityFrom, to_date: velocityTo });
      } else if (!cache[analyticsTab]?.data && !cache[analyticsTab]?.loading) {
        // Static tabs: csp-leaderboard, stellar-reseller, partner-tiers
        fetchData(analyticsTab, analyticsTab);
      }
    } else {
      // Operations tabs — fetch once and cache
      if (!cache[operationsTab]?.data && !cache[operationsTab]?.loading) {
        fetchData(operationsTab, OPS_PATH[operationsTab]);
      }
    }
  }, [topTab, analyticsTab, operationsTab, overviewFrom, overviewTo, velocityFrom, velocityTo]); // eslint-disable-line

  const jumpToOps = useCallback((opTab) => {
    setTopTab('operations');
    setOperationsTab(opTab);
  }, []);

  const getState = (key) => cache[key] || { data: null, loading: false, error: null };

  const renderContent = () => {
    if (topTab === 'analytics') {
      const key = analyticsTab;
      const { data, loading, error } = getState(key);
      if (loading) return <LoadingState />;
      if (error)   return <ErrorBanner message={error} />;
      if (!data)   return null;

      if (key === 'overview')          return <OverviewTab data={data} from={overviewFrom} to={overviewTo} onFrom={setOverviewFrom} onTo={setOverviewTo} onJumpToOps={jumpToOps} />;
      if (key === 'csp-leaderboard')   return <CSPLeaderboardTab data={data} />;
      if (key === 'stellar-reseller')  return <StellarResellerTab data={data} />;
      if (key === 'training-velocity') return <TrainingVelocityTab data={data} from={velocityFrom} to={velocityTo} onFrom={setVelocityFrom} onTo={setVelocityTo} />;
      if (key === 'partner-tiers')     return <PartnerTiersTab data={data} />;
    } else {
      const key = operationsTab;
      const { data, loading, error } = getState(key);
      if (loading) return <LoadingState />;
      if (error)   return <ErrorBanner message={error} />;
      if (!data)   return null;

      if (key === 'workshops')        return <WorkshopsTab data={data} />;
      if (key === 'private-delivery') return <PrivateDeliveryTab data={data} />;
      if (key === 'lab-requests')     return <LabRequestsTab data={data} />;
      if (key === 'syndication')      return <SyndicationTab data={data} />;
    }
    return null;
  };

  return (
    <div data-testid="skilling-in-a-box-report" style={{ padding: '0 0 32px' }}>
      {/* Page header */}
      <div style={{ padding: '20px 24px 0', marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <Package size={22} color={C.dark} />
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0e2e2e', letterSpacing: '-0.5px', margin: 0 }}>Skilling in a Box</h1>
              <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>Partner Program Analytics &amp; Operations</span>
            </div>
            <p style={{ fontSize: 13, color: '#64748b', margin: '2px 0 0' }}>Workshop delivery, partner performance, and programme operations across the LevelUp network.</p>
          </div>
        </div>
      </div>

      <div style={{ padding: '16px 24px 0' }}>
        {/* Top-level tab switcher (Analytics / Operations) */}
        <div data-testid="sib-top-tabs" style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {[{ id: 'analytics', label: 'Analytics' }, ...(piiAllowed ? [{ id: 'operations', label: 'Operations' }] : [])].map(t => (
            <button key={t.id} data-testid={`sib-top-tab-${t.id}`} onClick={() => setTopTab(t.id)}
              style={{ padding: '9px 24px', fontSize: 13, fontWeight: 700, border: 'none', borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s', background: topTab === t.id ? C.dark : '#f1f5f9', color: topTab === t.id ? C.green : '#64748b', boxShadow: topTab === t.id ? `0 2px 8px ${C.dark}30` : 'none' }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Sub-tabs */}
        <SubTabBar
          tabs={topTab === 'analytics' ? ANALYTICS_TABS : OPERATIONS_TABS}
          active={topTab === 'analytics' ? analyticsTab : operationsTab}
          onChange={topTab === 'analytics' ? setAnalyticsTab : setOperationsTab}
          testPrefix={topTab === 'analytics' ? 'sib-analytics-tab' : 'sib-ops-tab'}
        />

        {/* Tab content */}
        <div data-testid="sib-content">
          {renderContent()}
          {topTab === 'analytics' && (
            <OperationsRollupKPIs data={opsRollup.data} loading={opsRollup.loading} />
          )}
        </div>
      </div>
    </div>
  );
}
